/* eslint-disable no-nested-ternary */
/* eslint-disable no-console */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import ActivePositions from 'components/DashboardParts/ActivePositions';
import Notifications from 'components/DashboardParts/Notifications';
import CenteredLoader from 'components/Common/Loader';
import Table from 'components/Table';
import { clearKpisData, fetchKpis } from 'state/actions/kpis';
import { fetchProducts } from 'state/actions/products';
import { fetchPositions } from 'state/actions/positions';

const Home = () => {
  /**
   * after excute fetch functions, you find each function on userEffect
   * we get this data form state
   *
   * @param {array} activationsList get list of activations each activation
   * is a type of object contains contacts and products calculations
   *
   * @param {boolean} kpiLoading check if kpi of activation loaded
   *
   * @param {array} productsList get list of products from the root
   * collection 'products' to compare each product and his quantity
   * on each activation
   *
   * @param {boolean} productsLoading check if products loaded
   *
   * @param {integer} positionsCount get the count of all positions
   * from the root collection 'positions'
   *
   * @param {boolean} positionsCount check if positions loaded
   */
  const {
    activationsList,
    kpiLoading,
    productsList,
    productsLoading,
    positionsCount,
    positionsLoading,
  } = useSelector(
    (state) => ({
      activationsList: state.kpis.data,
      kpiLoading: state.kpis.loading,
      productsList: state.products.data,
      productsLoading: state.products.loading,
      positionsCount: state.positions.data.length,
      positionsLoading: state.positions.loading,
      error: state.kpis.error,
    }),
    shallowEqual
  );
  const dispatch = useDispatch();

  /**
   * Execute fetch function to get
   * activations KPIs (calculations)
   * postions to compare activated (status = 'VALIDATED') postions
   * products then show the quantity and price on all activations
   */
  useEffect(() => {
    dispatch(fetchKpis());
    dispatch(fetchPositions());
    dispatch(fetchProducts());
    return () => dispatch(clearKpisData());
  }, [dispatch]);

  /**
   * Columns show the contacts calculation of each activation
   */
  const actContactsColumns = [
    {
      Header: 'Nom ',
      accessor: 'name',
    },
    {
      Header: 'Nombre POS',
      accessor: 'initPositions',
    },
    {
      Header: 'Nombre des animations',
      accessor: 'animationNumber',
      /**
       * animation number means the number of positions assigned to the activation
       */
      Cell: ({ row }) => {
        return row.original.positions && row.original.positions.length;
      },
    },
    {
      Header: 'Ventes',
      accessor: 'totalProducts',
    },
    {
      Header: 'Nombre de contact',
      accessor: 'totalContacts',
    },
    {
      Header: 'Objectif C. Réussi',
      accessor: 'contactObjective',
    },
    {
      Header: 'Nombre de contact réussi',
      accessor: 'totalSuccessContacts',
    },
    {
      Header: '% atteinte Objectif',
      /**
       * atteinte Objectif is percentage of this activation
       * success contacts (dynamic, int) divided on contact objectif (static, int)
       */
      Cell: ({ row }) => {
        return (
          <small className="has-text-grey is-abbr-like">
            {(
              (row.original.totalSuccessContacts /
                row.original.contactObjective) *
              100
            ).toFixed(2)}{' '}
            %
          </small>
        );
      },
    },
    {
      Header: 'Prix agence',
      accessor: 'agencyPrice',
    },
    {
      Header: 'Coût opération',
      /**
       * Coût opération is sum of (quantity * price) of all this activation products
       */
      Cell: ({ row }) => {
        return (
          <small className="has-text-grey is-abbr-like">
            {row.original.products.reduce(
              (acc, actp) => acc + actp.quantity * actp.price,
              0
            )}
          </small>
        );
      },
    },
    /**
     * Cost per contact is (atteinte Objectif / Nombre de contact)
     */
    {
      Header: 'Cost per contact',
      Cell: ({ row }) => {
        return (
          <small className="has-text-grey is-abbr-like">
            {(row.original.totalContacts
              ? row.original.products.reduce(
                  (acc, actp) => acc + actp.quantity * actp.price,
                  0
                ) / row.original.totalContacts
              : 0
            ).toFixed(2)}
          </small>
        );
      },
    },
    {
      Header: 'Cost per succ. contact',
      /**
       * Cost per succ. contact' is (atteinte Objectif / Nombre de contact réussi)
       */
      Cell: ({ row }) => {
        return (
          <small className="has-text-grey is-abbr-like">
            {(row.original.totalSuccessContacts
              ? row.original.products.reduce(
                  (acc, actp) => acc + actp.quantity * actp.price,
                  0
                ) / row.original.totalSuccessContacts
              : 0
            ).toFixed(2)}
          </small>
        );
      },
    },
  ];

  /**
   * Columns show the name of activation then all podcuts on the App
   * Then get the "quantity" of products on each activation and show it on his
   * column name, if the product does not exists on activation return 0
   */
  const actProductsQuantityColumns = [
    {
      Header: 'Nom ',
      accessor: 'name',
    },
    ...(!productsLoading
      ? productsList.map((p) => ({
          Header: p.name,
          Cell: ({ row }) => (
            <span>
              {(
                row.original.products.find(
                  (actp) => actp.product.name === p.name
                ) || {}
              ).quantity || 0}
            </span>
          ),
        }))
      : []),
  ];

  /**
   * Columns show the name of activation then all podcuts on the App
   * Then get the "price" of products on each activation and show it on his
   * column name, if the product does not exists on activation return 0
   */
  const actProductsPriceColumns = [
    {
      Header: 'Nom ',
      accessor: 'name',
    },
    ...(!productsLoading
      ? productsList.map((p) => ({
          Header: p.name,
          Cell: ({ row }) => (
            <span>
              {(
                row.original.products.find(
                  (actp) => actp.product.name === p.name
                ) || {}
              ).price || 0}
            </span>
          ),
        }))
      : []),
  ];

  return (
    <div className="page-padding">
      <div className="flex">
        {/**
         * Left side of page contains claculation tables
         */}
        <div className="w-2/3 flex flex-col p-3">
          <h3 className="text-brand-darkBlue text-2xl font-semibold">
            ACTIVATIONS
          </h3>
          <div className="flex" style={{ overflow: 'auto' }}>
            <Table
              columns={actContactsColumns}
              data={activationsList.length > 0 ? activationsList : []}
              loading={kpiLoading}
            />
          </div>
          <h3 className="text-brand-darkBlue text-2xl font-semibold">
            SORTIE GOODIES
          </h3>
          <div className="flex">
            <Table
              columns={actProductsQuantityColumns}
              data={activationsList.length > 0 ? activationsList : []}
              loading={kpiLoading}
            />
          </div>
          <h3 className="text-brand-darkBlue text-2xl font-semibold">
            PRIX GOODIES
          </h3>
          <div className="flex">
            <Table
              columns={actProductsPriceColumns}
              data={activationsList.length > 0 ? activationsList : []}
              loading={kpiLoading}
            />
          </div>
        </div>
         {/**
         * Right side of page contains claculation of activated positions
         * the notification appear on real-time
         */}
        <div className="w-1/3">
          <div>
            {kpiLoading ? (
              <CenteredLoader />
            ) : !positionsLoading ? (
              <ActivePositions
                initial={positionsCount}
                activated={
                  activationsList.length > 0
                    ? activationsList.reduce(
                        (acc, curr) => acc + (curr.activatedPositions || 0),
                        0
                      )
                    : 0
                }
              />
            ) : (
              <ActivePositions initial={0} activated={0} />
            )}
          </div>
          <div className="">
            <Notifications />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
